"use client";

import { useEffect, useRef, useState } from "react";
import { trackEvent } from "@/lib/analytics/track";

const NO_TRACK_KEY = "nohaya-no-track";
const RATED_ITEMS_KEY = "nohaya-rated-items";
const SESSION_SHOWN_KEY = "nohaya-rating-shown-session";
const DWELL_MS = 5000;
const SCROLL_THRESHOLD_PCT = 50;

type ContentType = "resume" | "place" | "blog";

function isOptedOut(): boolean {
  try {
    return localStorage.getItem(NO_TRACK_KEY) === "1";
  } catch {
    return false;
  }
}

function hasBeenAskedAbout(key: string): boolean {
  try {
    const raw = localStorage.getItem(RATED_ITEMS_KEY);
    const set: string[] = raw ? JSON.parse(raw) : [];
    return set.includes(key);
  } catch {
    return false;
  }
}

function markAsked(key: string) {
  try {
    const raw = localStorage.getItem(RATED_ITEMS_KEY);
    const set: string[] = raw ? JSON.parse(raw) : [];
    if (!set.includes(key)) set.push(key);
    localStorage.setItem(RATED_ITEMS_KEY, JSON.stringify(set));
  } catch {
    // Best-effort suppression -- a write failure just means this item might
    // get asked about again, not a functional break.
  }
}

function hasShownThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_SHOWN_KEY) === "1";
  } catch {
    return false;
  }
}

function markShownThisSession() {
  try {
    sessionStorage.setItem(SESSION_SHOWN_KEY, "1");
  } catch {
    // no-op
  }
}

export default function RatingPrompt({
  contentType,
  contentSlug,
  label,
}: {
  contentType: ContentType;
  contentSlug: string;
  label: string;
}) {
  const [visible, setVisible] = useState(false);
  const [rated, setRated] = useState(false);
  const [hovered, setHovered] = useState(0);
  const [locked, setLocked] = useState(0);
  const firedRef = useRef(false);

  useEffect(() => {
    const itemKey = `${contentType}:${contentSlug}`;
    if (isOptedOut() || hasBeenAskedAbout(itemKey) || hasShownThisSession()) return;

    function trigger() {
      if (firedRef.current) return;
      firedRef.current = true;
      markShownThisSession();
      setVisible(true);
    }

    const timer = setTimeout(trigger, DWELL_MS);

    function onScroll() {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      const pct = scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100;
      if (pct >= SCROLL_THRESHOLD_PCT) trigger();
    }
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [contentType, contentSlug]);

  function submitRating(rating: number) {
    if (locked) return;
    setLocked(rating);
    markAsked(`${contentType}:${contentSlug}`);
    // Brief delay so the filled-stars state is actually visible before
    // swapping to the thank-you message, instead of an instant jump cut.
    setTimeout(() => {
      trackEvent("content_rating", { contentType, contentSlug, rating });
      setRated(true);
      setTimeout(() => setVisible(false), 1500);
    }, 350);
  }

  function dismiss() {
    markAsked(`${contentType}:${contentSlug}`);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 w-[calc(100vw-3rem)] max-w-xs rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-800 dark:bg-[#0f172a]">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
        </svg>
      </button>

      {rated ? (
        <p className="pr-4 text-sm text-slate-600 dark:text-slate-400">Thanks for the feedback!</p>
      ) : (
        <>
          <p className="pr-4 text-sm font-medium text-slate-900 dark:text-white">Was this {label} helpful?</p>
          <div className="mt-2 flex gap-1" onMouseLeave={() => setHovered(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => submitRating(n)}
                onMouseEnter={() => setHovered(n)}
                aria-label={`Rate ${n} out of 5`}
                className={`text-2xl transition hover:scale-110 ${
                  n <= (locked || hovered) ? "text-amber-400" : "text-slate-300 dark:text-slate-600"
                }`}
              >
                ★
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
